'use client';

/**
 * @fileoverview Right-anchored, resizable chat side panel.
 *
 * Intentionally not built on the Radix Dialog-based Sheet primitive:
 * Radix Dialog assumes modal semantics (overlay, focus trap,
 * body-scroll lock), which fight the core UX we want — the panel
 * should coexist with the editor so the author can read and chat
 * simultaneously. Instead we render a fixed, non-modal panel positioned
 * below the 56px global header, with its own slide-in animation via
 * Tailwind transitions.
 *
 * The left edge is a drag handle that resizes the panel. Width is
 * persisted in localStorage via ChatPanelContext.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useProject } from '@/context/project-context';
import { useChatPanel } from '@/context/chat-panel-context';
import { useChatSessions } from '@/domains/chat/hooks/use-chat-sessions';
import { useChatStream } from '@/domains/chat/hooks/use-chat-stream';
import type { ChatContextHint } from '@/domains/chat/schemas';
import { cn } from '@/lib/utils';
import { ChatInput } from './chat-input';
import { MessageList } from './message-list';
import { SessionPicker } from './session-picker';

// Global header is sticky at h-14 (56px). Panel sits below it so both
// remain interactive.
const HEADER_OFFSET_PX = 56;

export function ChatPanel() {
  const { project } = useProject();
  const {
    isOpen,
    close,
    width,
    setWidth,
    minWidth,
    maxWidth,
    activeSessionId,
    setActiveSessionId,
  } = useChatPanel();

  const {
    sessions,
    loadSession,
    renameSession,
    deleteSession,
    upsertLocalSummary,
    bumpLocalSummary,
    refresh,
  } = useChatSessions(project.id);

  const {
    messages,
    streamingMessage,
    isStreaming,
    error,
    sendMessage,
    hydrate,
    clear,
  } = useChatStream({
    projectId: project.id,
    activeSessionId,
    onSessionCreated: (sessionId, title) => {
      setActiveSessionId(sessionId);
      upsertLocalSummary({
        id: sessionId,
        projectId: project.id,
        title,
        messageCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    },
    onTurnPersisted: (sessionId) => {
      // Two messages per turn (user + assistant).
      bumpLocalSummary(sessionId, 2);
    },
  });

  // ── Load selected session's messages ────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    if (!activeSessionId) {
      clear();
      return;
    }
    let cancelled = false;
    void (async () => {
      const session = await loadSession(activeSessionId);
      if (!cancelled && session) {
        hydrate(session.messages ?? []);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `hydrate` and `clear` are stable; `loadSession` is memoized on
    // projectId. Activity only triggers when the selected session changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionId, isOpen]);

  // ── Refresh session list when the panel opens ───────────────────────
  useEffect(() => {
    if (isOpen) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Resize handle ───────────────────────────────────────────────────
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ startX: number; startWidth: number } | null>(
    null,
  );

  const onHandleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      dragStartRef.current = { startX: e.clientX, startWidth: width };
    },
    [width],
  );

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const start = dragStartRef.current;
      if (!start) return;
      // Dragging left (smaller clientX) should make the panel wider
      // since the panel is anchored to the right.
      const delta = start.startX - e.clientX;
      setWidth(start.startWidth + delta);
    };
    const onUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, setWidth]);

  // ── Derived context hint ────────────────────────────────────────────
  const contextHint = useMemo<ChatContextHint | undefined>(() => {
    const hint: ChatContextHint = {};
    if (project.activeChapterId) hint.chapterId = project.activeChapterId;
    if (project.activeCharacterId) hint.characterId = project.activeCharacterId;
    if (project.activeWorldElementId) {
      hint.worldElementId = project.activeWorldElementId;
    }
    return Object.keys(hint).length > 0 ? hint : undefined;
  }, [
    project.activeChapterId,
    project.activeCharacterId,
    project.activeWorldElementId,
  ]);

  // ── Handlers ────────────────────────────────────────────────────────
  const handleSend = useCallback(
    (text: string) => {
      void sendMessage(text, contextHint);
    },
    [sendMessage, contextHint],
  );

  const handleNewChat = useCallback(() => {
    setActiveSessionId(null);
    clear();
  }, [clear, setActiveSessionId]);

  const handleSelectSession = useCallback(
    (sessionId: string) => {
      setActiveSessionId(sessionId);
    },
    [setActiveSessionId],
  );

  const handleDelete = useCallback(
    async (sessionId: string) => {
      await deleteSession(sessionId);
      if (sessionId === activeSessionId) {
        setActiveSessionId(null);
        clear();
      }
    },
    [activeSessionId, clear, deleteSession, setActiveSessionId],
  );

  const activeTitle = useMemo(() => {
    if (!activeSessionId) return 'New chat';
    return (
      sessions.find((s) => s.id === activeSessionId)?.title ?? 'Chat'
    );
  }, [activeSessionId, sessions]);

  // Panel is always in the tree so CSS transitions animate on
  // open/close. `pointer-events-none` when closed prevents it from
  // intercepting clicks on the editor.
  return (
    <aside
      aria-label="Chat with your story"
      aria-hidden={!isOpen}
      className={cn(
        'fixed right-0 z-40 flex border-l bg-background shadow-xl transition-transform duration-300 ease-out',
        isOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none',
      )}
      style={{
        top: HEADER_OFFSET_PX,
        bottom: 0,
        width,
      }}
    >
      {/* Drag handle — left edge, 6px hit area for comfort. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize chat panel"
        aria-valuemin={minWidth}
        aria-valuemax={maxWidth}
        aria-valuenow={width}
        onMouseDown={onHandleMouseDown}
        className={cn(
          'group absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize -translate-x-1/2',
          isDragging && 'bg-primary/40',
        )}
      >
        <div
          className={cn(
            'absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border',
            'group-hover:bg-primary/60 transition-colors',
            isDragging && 'bg-primary',
          )}
        />
      </div>

      <div className="flex flex-1 flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-3 py-2">
          <SessionPicker
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSelect={handleSelectSession}
            onNewChat={handleNewChat}
            onRename={renameSession}
            onDelete={handleDelete}
            activeTitle={activeTitle}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={close}
            aria-label="Close chat panel"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Messages */}
        <MessageList
          messages={messages}
          streamingMessage={streamingMessage}
          emptyState={<EmptyState />}
        />

        {/* Non-fatal error banner */}
        {error && (
          <div className="border-t border-destructive/30 bg-destructive/10 px-4 py-2 text-xs text-destructive">
            {error}
          </div>
        )}

        {/* Composer */}
        <ChatInput onSend={handleSend} disabled={isStreaming} />
      </div>
    </aside>
  );
}

// =============================================================================
// EMPTY STATE
// =============================================================================

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center px-8 text-center text-muted-foreground">
      <MessageSquare className="mb-3 h-10 w-10 opacity-50" />
      <p className="text-sm font-medium text-foreground mb-1">
        Chat with your story
      </p>
      <p className="text-xs leading-relaxed max-w-xs">
        Think out loud about characters, chapters, or plot threads. I've read
        your manuscript — ask me anything.
      </p>
    </div>
  );
}
